import { C } from "../theme";
import { useMediaQuery, MOBILE_QUERY } from "../lib/useMediaQuery";

interface Param {
  name: string;
  req?: boolean;
  type: string;
  desc: React.ReactNode;
}

const SEARCH_PARAMS: Param[] = [
  { name: "q", req: true, type: "string", desc: "Free-text address or place name to resolve." },
  { name: "format", type: "enum", desc: (
    <>Response format: <code style={{ color: C.text }}>json</code> (default) or <code style={{ color: C.text }}>geojson</code>.</>
  ) },
  { name: "limit", type: "int", desc: (
    <>Max results, 1–40. Default <code style={{ color: C.text }}>10</code>.</>
  ) },
  { name: "countrycodes", type: "string", desc: (
    <>Comma-separated ISO 3166-1 codes to restrict results, e.g. <code style={{ color: C.text }}>de,fr</code>.</>
  ) },
  { name: "addressdetails", type: "0 | 1", desc: "Include a breakdown of address components." },
];

const REVERSE_PARAMS: Param[] = [
  { name: "lat", req: true, type: "float", desc: "Latitude, -90 to 90." },
  { name: "lon", req: true, type: "float", desc: "Longitude, -180 to 180." },
  { name: "zoom", type: "int", desc: (
    <>Detail level 0–18 (building → country). Default <code style={{ color: C.text }}>18</code>.</>
  ) },
];

const RESPONSE_FIELDS: [string, string][] = [
  ["lat, lon", "Coordinates as strings."],
  ["display_name", "Full formatted address."],
  ["class, type", "Category & subtype of the result."],
  ["importance", "Relevance score, 0–1."],
  ["boundingbox", "[minLat, maxLat, minLon, maxLon]."],
];

const STATUS_CODES: [string, string, React.ReactNode][] = [
  ["200", C.ac, "OK — array of matches (may be empty)."],
  ["400", C.warn, "Bad request — missing/invalid params."],
  ["401", "#e0a0a0", "Unauthorized — bad or missing API key."],
  ["429", "#e0a0a0", (
    <>Rate limited — back off, retry after the <code style={{ color: C.text }}>Retry-After</code> header.</>
  )],
];

function Endpoint({ method, path, blurb, params, isMobile }: { method: string; path: string; blurb: string; params: Param[]; isMobile: boolean }) {
  const grid = "130px 90px 1fr";
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 8, overflow: "hidden", marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "13px 16px", background: C.panelAlt, borderBottom: `1px solid ${C.line}` }}>
        <span style={{ background: "rgba(54,224,127,.12)", color: C.ac, fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 4 }}>{method}</span>
        <code style={{ color: C.textBright, fontSize: 13.5 }}>{path}</code>
        <span style={{ color: C.mutedDim, fontSize: 12, marginLeft: isMobile ? 0 : "auto", flexBasis: isMobile ? "100%" : "auto" }}>{blurb}</span>
      </div>
      <div style={{ padding: "6px 16px" }}>
        {!isMobile && (
          <div style={{ display: "grid", gridTemplateColumns: grid, gap: 10, padding: "11px 0", color: C.mutedDim, fontSize: 11, letterSpacing: ".06em" }}>
            <span>PARAMETER</span><span>TYPE</span><span>DESCRIPTION</span>
          </div>
        )}
        {params.map((p) =>
          isMobile ? (
            <div key={p.name} style={{ padding: "11px 0", borderTop: `1px solid ${C.line}`, fontSize: 12.5 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                <code style={{ color: C.ac }}>
                  {p.name}{p.req && <span style={{ color: "#e07a7a", fontSize: 10 }}> *req</span>}
                </code>
                <span style={{ color: C.muted, fontSize: 11 }}>{p.type}</span>
              </div>
              <span style={{ color: C.soft }}>{p.desc}</span>
            </div>
          ) : (
            <div key={p.name} style={{ display: "grid", gridTemplateColumns: grid, gap: 10, padding: "10px 0", borderTop: `1px solid ${C.line}`, fontSize: 12.5, alignItems: "baseline" }}>
              <code style={{ color: C.ac }}>
                {p.name}{p.req && <span style={{ color: "#e07a7a", fontSize: 10 }}> *req</span>}
              </code>
              <span style={{ color: C.muted }}>{p.type}</span>
              <span style={{ color: C.soft }}>{p.desc}</span>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

export function ApiReference() {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  return (
    <div id="api-docs" style={{ background: C.panel, border: `1px solid ${C.line2}`, borderRadius: 10, padding: isMobile ? 18 : 26, marginTop: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
        <span style={{ color: C.textBright, fontWeight: 700, fontSize: 16, whiteSpace: "nowrap" }}>API Reference</span>
        <span style={{ color: C.mutedDim, fontSize: 12, textAlign: "right" }}>
          v1 · base <code style={{ color: C.muted }}>https://geo.zynday.com/v1</code>
        </span>
      </div>
      <div style={{ color: C.muted, fontSize: 12.5, lineHeight: 1.7, marginBottom: 22, maxWidth: 620 }}>
        All requests require an <code style={{ color: C.ac }}>Authorization: Bearer &lt;key&gt;</code> header. Responses are JSON. When you show results, credit “© OpenStreetMap contributors” — required by the data licence (ODbL).
      </div>

      <Endpoint method="GET" path="/v1/search" blurb="look up a place by name or address" params={SEARCH_PARAMS} isMobile={isMobile} />
      <Endpoint method="GET" path="/v1/reverse" blurb="find the address at a set of coordinates" params={REVERSE_PARAMS} isMobile={isMobile} />

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 18 }}>
        <div>
          <div style={{ color: C.textBright, fontWeight: 600, fontSize: 13, marginBottom: 12 }}>Response fields</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9, fontSize: 12.5 }}>
            {RESPONSE_FIELDS.map(([f, d]) => (
              <div key={f} style={{ display: "flex", gap: 10 }}>
                <code style={{ color: C.ac, flex: "none", width: 120 }}>{f}</code>
                <span style={{ color: C.soft }}>{d}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div style={{ color: C.textBright, fontWeight: 600, fontSize: 13, marginBottom: 12 }}>Status codes</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9, fontSize: 12.5 }}>
            {STATUS_CODES.map(([code, color, d]) => (
              <div key={code} style={{ display: "flex", gap: 10 }}>
                <code style={{ color, flex: "none", width: 56 }}>{code}</code>
                <span style={{ color: C.soft }}>{d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
